import { useEffect } from 'react';
import { useAuth } from '@src/auth/AuthContext';
import { refreshAccessToken } from '@src/auth/authCodeFlow';
import { useTerminalAccount } from '@src/context/AccountContext';
import { useExternalUser } from '@src/context/ExternalUserContext';
import { useTheme } from '@src/context/ThemeContext';
import { useSetInitLang } from '@src/i18n';
import { hostBridge } from '@src/messaging/hostBridge';
import { isOriginAllowed } from '@src/messaging/originAllowlist';
import {
    isInboundMessage,
    isInitPayload,
    sanitizeInitPayload
} from '@src/messaging/protocol';
import type { InitPayload } from '@src/messaging/protocol';

export function usePostMessageAuth() {
    const { setTokens, markBootReady } = useAuth();
    const { startInit } = useTerminalAccount();
    const { setTheme } = useTheme();
    const setInitLang = useSetInitLang();
    const { setExternalUser } = useExternalUser();

    useEffect(() => {
        // Latest in-flight exchange's refresh token. Used to ignore stale
        // responses if multiple init messages arrive in quick succession.
        let activeRefreshToken: string | null = null;

        async function attemptSilentExchange(refreshToken: string) {
            activeRefreshToken = refreshToken;
            try {
                const response = await refreshAccessToken(refreshToken);

                if (activeRefreshToken !== refreshToken) return;

                const newRefreshToken = response.refresh_token ?? refreshToken;
                const expiresAt = new Date(
                    Date.now() + response.expires_in * 1000
                ).toISOString();

                setTokens({
                    accessToken: response.access_token,
                    refreshToken: newRefreshToken,
                    expiresAt,
                    mode: 'webview'
                });

                hostBridge.sessionToken({ refreshToken: newRefreshToken });
            } catch {
                // Exchange failed — mark boot ready so the app proceeds to OAuth login.
                markBootReady();
            }
        }

        function handleInit(payload: InitPayload) {
            startInit({
                companyId: payload.companyId,
                applicationName: payload.applicationName
            });

            if (payload.theme) {
                setTheme(payload.theme, 'host');
            }

            if (payload.lang) {
                setInitLang(payload.lang);
            }

            if (payload.externalUserId) {
                setExternalUser({
                    externalUserId: payload.externalUserId
                });
            }

            if (payload.refreshToken) {
                void attemptSilentExchange(payload.refreshToken);
            } else {
                // No refresh token — mark ready to proceed to OAuth login.
                markBootReady();
            }
        }

        function handleMessage(event: MessageEvent) {
            if (!isOriginAllowed(event.origin)) return;

            const data = event.data;
            if (!isInboundMessage(data)) return;

            if (data.type === '7punches:init') {
                if (!isInitPayload(data.payload)) return;

                // Pin outbound messages to the validated origin so refreshed
                // refresh tokens cannot leak to a different parent.
                hostBridge.setTargetOrigin(event.origin);

                handleInit(sanitizeInitPayload(data.payload));
                return;
            }

            if (data.type === '7punches:set_external_user') {
                setExternalUser({
                    externalUserId: data.payload.externalUserId
                });
            }
        }

        window.addEventListener('message', handleMessage);

        // Tell the host the listener is installed. Without this handshake the
        // host might post `7punches:init` before React has mounted and we'd
        // miss it entirely.
        hostBridge.ready();

        return () => {
            window.removeEventListener('message', handleMessage);
            activeRefreshToken = null;
        };
    }, [setTokens, startInit, setTheme, setInitLang, markBootReady, setExternalUser]);
}
